import { useCallback, useEffect, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import { PaymentAndInsuranceKnowledge } from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/ai_settings.pb';
import dayjs from 'dayjs';
import cloneDeep from 'lodash-es/cloneDeep';
import isEqual from 'lodash-es/isEqual';
import { useGetAirSettingsQuery, useUpdatePaymentInsuranceAirSettingsMutation } from '@frontend/api-ai-settings';
import { Trans, useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { FloatingSaveBar } from '@frontend/settings-ui';
import { sentry } from '@frontend/tracking';
import { useUnsavedChanges } from '@frontend/unsaved-changes-modal';
import { useAlert, useModalControl } from '@frontend/design-system';
import { AIRSettingsDefaultSourcesProvider } from '../../../../contexts/air-settings-default-sources.context';
import {
  useAIRPaymentKnowledgeStore,
  AIRPaymentKnowledgeProvider,
  selectStoreIsDirty,
} from '../../../../stores/air-settings.store';
import { AiReceptionistTrackingIds } from '../../../../tracking-ids';
import { LastUpdatedInfo } from '../../common/last-updated-info';
import { SettingsUnsavedChangesModal } from '../../common/settings-unsaved-changes-modal';
import { MultiLocationPaymentKnowledge } from './multi-location-payment-knowledge';
import { SingleLocationPaymentKnowledge } from './single-location-payment-knowledge';
import { sanitizePaymentKnowledgeData } from './utils';

const DEFAULT_PAYMENT_KNOWLEDGE: PaymentAndInsuranceKnowledge = {};

const createInitialSettings = (locationIds: string[]): Record<string, PaymentAndInsuranceKnowledge> => {
  return locationIds.reduce((acc, id) => {
    acc[id] = cloneDeep(DEFAULT_PAYMENT_KNOWLEDGE);
    return acc;
  }, {} as Record<string, PaymentAndInsuranceKnowledge>);
};

const PaymentKnowledgePageContent = () => {
  const { t } = useTranslation('ai-receptionist-settings');
  const { selectedLocationIds } = useAppScopeStore();
  const alerts = useAlert();

  const { initialSettings, draftSettings, setInitialSettings, setDraftSettings, saveDraft, clearDraft, resetStore } =
    useAIRPaymentKnowledgeStore([
      'initialSettings',
      'draftSettings',
      'setInitialSettings',
      'setDraftSettings',
      'saveDraft',
      'clearDraft',
      'resetStore',
    ]);

  const shouldProceedAfterSaveRef = useRef(false);

  const { data: airSettingsResponse, isLoading: isPaymentSettingsLoading } = useGetAirSettingsQuery({
    request: {
      locationIds: selectedLocationIds,
    },
    options: {
      enabled: selectedLocationIds.length > 0,
    },
  });

  const { mutate: updatePaymentInsuranceAirSettings, isLoading: isUpdatingPaymentInsuranceAirSettings } =
    useUpdatePaymentInsuranceAirSettingsMutation({
      options: {
        onSuccess: () => {
          alerts.success(t('Payment knowledge settings saved successfully.'));
          saveDraft();
          if (shouldProceedAfterSaveRef.current) {
            shouldProceedAfterSaveRef.current = false;
            proceedWithUnsavedChanges();
          }
        },
        onError: (error) => {
          shouldProceedAfterSaveRef.current = false;
          alerts.error(t('Failed to save payment knowledge settings. Please try again.'));
          sentry.error({
            team: 'frontend',
            topic: 'ai-receptionist',
            error,
          });
        },
      },
    });

  useEffect(() => {
    const paymentSettings = airSettingsResponse?.locationAirSettings?.reduce((acc, curr) => {
      if (curr.locationId && curr.airSettings?.paymentAndInsuranceKnowledge) {
        acc[curr.locationId] = sanitizePaymentKnowledgeData(curr.airSettings.paymentAndInsuranceKnowledge);
      }
      return acc;
    }, {} as Record<string, PaymentAndInsuranceKnowledge>);

    if (paymentSettings && Object.keys(paymentSettings).length > 0) {
      setInitialSettings(paymentSettings);
      setDraftSettings(cloneDeep(paymentSettings));
    } else {
      const defaultSettings = createInitialSettings(selectedLocationIds);
      setInitialSettings(defaultSettings);
      setDraftSettings(cloneDeep(defaultSettings));
    }
  }, [airSettingsResponse, selectedLocationIds, setInitialSettings, setDraftSettings]);

  const isMultiLocation = selectedLocationIds.length > 1;

  const locationErrors = useAIRPaymentKnowledgeStore((state) => {
    return selectedLocationIds.reduce((acc, id) => {
      const errors = state.fieldErrors[id];
      acc[id] = errors ? Object.values(errors).some(Boolean) : false;
      return acc;
    }, {} as Record<string, boolean>);
  });

  const formHasErrors = useAIRPaymentKnowledgeStore((state) =>
    selectedLocationIds.some((id) => {
      const errors = state.fieldErrors[id];
      return errors ? Object.values(errors).some(Boolean) : false;
    })
  ) as boolean;
  const isDirty = useAIRPaymentKnowledgeStore(selectStoreIsDirty);

  const { modalProps: unsavedChangesModalProps, openModal: openUnsavedChangesModal } = useModalControl();
  const { proceed: proceedWithUnsavedChanges, cancel: cancelUnsavedChanges } = useUnsavedChanges(
    isDirty,
    unsavedChangesModalProps.show,
    openUnsavedChangesModal
  );

  const handleSave = useCallback(() => {
    if (formHasErrors || isUpdatingPaymentInsuranceAirSettings) return;

    const updatedLocationDataList = Object.entries(draftSettings)
      .filter(([locationId, settings]) => {
        const initialSettingsForLocation = initialSettings[locationId] || {};
        return !isEqual(settings, initialSettingsForLocation);
      })
      .map(([locationId, settings]) => ({
        ...settings,
        locationId,
      }));

    updatePaymentInsuranceAirSettings({
      paymentAndInsuranceKnowledge: updatedLocationDataList,
    });
  }, [
    formHasErrors,
    isUpdatingPaymentInsuranceAirSettings,
    draftSettings,
    updatePaymentInsuranceAirSettings,
    initialSettings,
  ]);

  const handleProceedWithoutSaving = () => {
    clearDraft();
    unsavedChangesModalProps.onClose();
    proceedWithUnsavedChanges();
  };

  const lastUpdatedAt = useMemo(() => {
    const lastUpdatedAtList = airSettingsResponse?.locationAirSettings
      ?.map((settings) =>
        settings?.airSettings?.paymentAndInsuranceKnowledge?.lastUpdatedAt
          ? dayjs(settings.airSettings.paymentAndInsuranceKnowledge.lastUpdatedAt)
          : undefined
      )
      .filter((ts): ts is dayjs.Dayjs => !!ts)
      .sort((a, b) => b.valueOf() - a.valueOf());

    return lastUpdatedAtList?.[0]?.toISOString();
  }, [airSettingsResponse?.locationAirSettings]);

  useEffect(() => {
    return () => {
      resetStore();
    };
  }, []);

  const isLoading = isPaymentSettingsLoading || isUpdatingPaymentInsuranceAirSettings;

  return (
    <div css={styles.container}>
      <div css={{ flex: 1, paddingBottom: '80px' }}>
        {isMultiLocation ? (
          <MultiLocationPaymentKnowledge
            locationIds={selectedLocationIds}
            settings={draftSettings}
            initialSettings={initialSettings}
            locationErrors={locationErrors}
            isLoading={isLoading}
          />
        ) : (
          <SingleLocationPaymentKnowledge locationId={selectedLocationIds[0]} isLoading={isLoading} />
        )}
      </div>

      <FloatingSaveBar
        isDirty={isDirty}
        hasErrors={formHasErrors}
        extraContent={lastUpdatedAt ? <LastUpdatedInfo lastUpdatedAt={lastUpdatedAt} /> : null}
        primaryAction={{
          label: t('Save'),
          onClick: handleSave,
          disabled: formHasErrors,
          trackingId: AiReceptionistTrackingIds.paymentsKnowledgeSaveButton,
          hoverLabel: t('Click to update AI knowledge base. Changes should apply soon after being updated.'),
          isLoading,
        }}
      />

      <SettingsUnsavedChangesModal
        show={unsavedChangesModalProps.show}
        onClose={() => {
          unsavedChangesModalProps.onClose();
          cancelUnsavedChanges();
        }}
        onProceedWithoutSaving={handleProceedWithoutSaving}
        onSave={() => {
          shouldProceedAfterSaveRef.current = true;
          handleSave();
          unsavedChangesModalProps.onClose();
        }}
        isSaveDisabled={formHasErrors}
        description={
          <Trans t={t}>
            Before you leave, <strong>would you like to save and push changes</strong> to your AI Receptionist's payment
            and insurance knowledge?
          </Trans>
        }
      />
    </div>
  );
};

export const PaymentKnowledgePage = () => {
  const { selectedLocationIds } = useAppScopeStore();

  return (
    <AIRSettingsDefaultSourcesProvider locationIds={selectedLocationIds}>
      <AIRPaymentKnowledgeProvider>
        <PaymentKnowledgePageContent />
      </AIRPaymentKnowledgeProvider>
    </AIRSettingsDefaultSourcesProvider>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    position: relative;
    height: 100%;
  `,
};
